import * as React from 'react';
import { CgClose } from 'react-icons/cg';
import styled from 'styled-components';

import { fetchWidgetInfo } from '../../actions';
import useApi from '../../actions/useApi';
import { useAppContext } from '../../appContext';
import EmojiWhite from '../../assets/emoji.svg';
import { closeUserForm } from '../../store/actions';
import { useStateContext } from '../../store/stateContext';
import { Product, User } from '../../types';
import CommentInput from '../comment-input';

interface IPostPurchaseProps {
    widgetId: string;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: auto;
    max-width: 100%;
    position: relative;
    overflow: visible;
    
    @media (max-width: 768px) {
        width: 100%;
        padding: 0;
        overflow: hidden;
    }
`;

const CloseButton = styled.button<{ isDarkMode: boolean; isMediumScreen: boolean }>`
    border: transparent !important;
    height: 32px !important;
    font-size: 12px !important;
    padding: 8px !important; /* 0.5rem = 8px */
    border-radius: 24px !important; /* 1.5rem = 24px */
    display: flex !important;
    flex-direction: row !important;
    gap: 4px !important; /* 0.25rem = 4px */
    padding-left: 16px !important; /* 1rem = 16px */
    padding-right: 16px !important; /* 1rem = 16px */
    align-items: center !important;
    justify-content: center !important;
    align-self: flex-end !important;
    margin-right: ${props => props.isMediumScreen ? '0' : '4px'} !important; /* 0.25rem = 4px */
    background-color: ${props => {
        if (!props.isMediumScreen) {
            return '#5942D1';
        }
        return props.isDarkMode ? 'white' : '#585568';
    }} !important;
    color: ${props => {
        if (!props.isMediumScreen) {
            return 'white';
        }
        return props.isDarkMode ? 'black' : 'white';
    }} !important;
    &:hover {
        background-color: ${props => {
        if (!props.isMediumScreen) {
            return '#5942D1';
        }
        return props.isDarkMode ? 'white' : '#585568';
    }} !important;
        color: ${props => {
        if (!props.isMediumScreen) {
            return 'white';
        }
        return props.isDarkMode ? 'black' : 'white';
    }} !important;
    }
`;

const ButtonText = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    color: ${props => props.isDarkMode ? 'black' : 'white'};
`;

const MainCard = styled.div<{ backgroundColor: string; widgetAlignment: string; isUserFormOpen: boolean }>`
    background-color: ${props => props.backgroundColor} !important;
    border-radius: ${props => {
        if (window.innerWidth >= 768) {
            if (props.widgetAlignment === 'leftcenter') {
                return '0 16px 16px 0'; /* 1rem = 16px */
            } else if (props.widgetAlignment === 'rightcenter') {
                return '16px 0 0 16px'; /* 1rem = 16px */
            } else {
                return '16px'; /* 1rem = 16px */
            }
        } else {
            if (props.widgetAlignment === 'leftbottom' || props.widgetAlignment === 'rightbottom') {
                return '24px 24px 0 0'; /* 1.5rem = 24px */
            } else if (props.widgetAlignment === 'leftcenter') {
                return '0 24px 24px 0'; /* 1.5rem = 24px */
            } else if (props.widgetAlignment === 'rightcenter') {
                return '24px 0 0 24px'; /* 1.5rem = 24px */
            }
            return '24px'; /* 1.5rem = 24px */
        }
    }} !important;
    ${props => props.isUserFormOpen && 'height: 480px !important;'}
    padding: 16px !important; /* 1rem = 16px */
    margin-bottom: 8px !important; /* 0.5rem = 8px */
    margin-top: 8px !important; /* 0.5rem = 8px */
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important; /* 1rem = 16px */
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    width: 360px !important;
    box-sizing: border-box !important;
    
    @media (max-width: 768px) {
        width: 100% !important;
        max-width: 100% !important;
    }
`;

const InfoWindow = styled.div<{ bgColor: string }>`
    background-color: ${props => props.bgColor} !important;
    border-radius: 16px !important; /* 1rem = 16px */
    padding: 12px 16px !important; /* 0.75rem = 12px, 1rem = 16px */
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important; /* 0.25rem = 4px */
`;

const Title = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    display: flex !important;
    flex-direction: row !important;
    gap: 8px !important; /* 0.5rem = 8px */
    align-items: center !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    margin: 0 !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
`;

const TitleImage = styled.img<{ invertForDarkMode: boolean }>`
    filter: ${props => props.invertForDarkMode ? 'invert(1)' : 'none'};
`;

const Message = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    font-size: 14px !important; /* 0.875rem = 14px */
    margin: 0 !important;
    margin-top: 2px !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'} !important;
`;

const DoneButton = styled.div`
    border-radius: 12px !important; /* 0.75rem = 12px */
    background-color: ${() => window.innerWidth >= 768 ? '#664EE1' : '#23C68D'} !important;
    padding: 0 16px !important; /* 1rem = 16px */
    padding-top: 8px !important; /* 0.5rem = 8px */
    padding-bottom: 8px !important; /* 0.5rem = 8px */
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
`;

const DoneText = styled.p`
    all: revert;
    color: white;
`;

const PostPurchase: React.FunctionComponent<IPostPurchaseProps> = ({ widgetId }) => {
    const { state, dispatch } = useStateContext();
    const { addComment } = useApi();
    const [success, setSuccess] = React.useState(false);
    const isUserFormOpen = state.userForm.isOpen;
    const { widgetAlignment, togglePurchaseMode, toggleUserInteraction } = useAppContext();
    const handleSubmit = async (message: string, values: Partial<User> | undefined) => {
        const productId = localStorage.getItem('previous_page');
        const product: Product = await fetchWidgetInfo(widgetId, `${productId}`) as Product;
        await addComment(`${product.id}`, message, values, undefined, true);
        setSuccess(true);
        toggleUserInteraction()
    }
    const onDoneClick = () => {
        togglePurchaseMode()
        dispatch(closeUserForm());
    }
    const [isMediumScreen, setIsMediumScreen] = React.useState(false);
    const checkScreenSize = () => {
        setIsMediumScreen(window.innerWidth >= 768); // Tailwind's 'md' breakpoint is 768px
    };

    React.useEffect(() => {
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);
    const isDarkMode = state.themeMode === 'dark';
    console.log({ isDarkMode }, state.themeMode)
    const message = state.productInfo?.post_purchase_pop_up_message ?? 'Tell us what you think'
    const successMessage = state.productInfo?.post_purchase_pop_up_success_message ?? 'Thanks for youre review !, We have sent promo code to your email'
    const title = state.productInfo?.post_purchase_pop_up_title ?? 'Why did you purchase this product?'

    const getBackgroundColor = () => {
        if (!isMediumScreen) {
            return '#5942D1';
        }
        return isDarkMode ? 'white' : '#585568';
    };

    const getInfoWindowBg = () => {
        if (!isMediumScreen) {
            return '#664EE1';
        }
        return isDarkMode ? '#EDF0F1' : '#625F74';
    }

    return (
        <Container>
            {!success && (
                <CloseButton
                    isDarkMode={isDarkMode}
                    isMediumScreen={isMediumScreen}
                    onClick={onDoneClick}
                >
                    <CgClose className='h-[16px] w-[16px]' />
                    <ButtonText isDarkMode={isDarkMode}>Close</ButtonText>
                </CloseButton>
            )}
            <MainCard backgroundColor={getBackgroundColor()} widgetAlignment={widgetAlignment} isUserFormOpen={isUserFormOpen}>
                <InfoWindow bgColor={getInfoWindowBg()}>
                    <Title isDarkMode={isDarkMode}>
                        <TitleImage
                            src={EmojiWhite}
                            invertForDarkMode={isMediumScreen && isDarkMode}
                        />
                        {success ? 'Success!' : title}
                    </Title>
                    <Message isDarkMode={isDarkMode}>
                        {success ? successMessage : message}
                    </Message>
                </InfoWindow>
                {
                    success ? (
                        <DoneButton onClick={onDoneClick}>
                            <DoneText>Done</DoneText>
                        </DoneButton>
                    ) : (
                        <CommentInput placeholder='Type here' onSubmit={handleSubmit} label='Post my message' />
                    )
                }
            </MainCard>
        </Container>
    )
};

export default PostPurchase;
